import type { ThemeConfig } from "antd";
import { theme } from "antd";

// ──────────────────────────────────────────────────────────────────────────
// NetScope Console — paired Dark / Light themes
//
//   Dark (default):
//     Primary    Cyan          #36c5f0
//     Surface    Dark navy     #0b1020 on #070a14
//     Sidebar    Deep navy     #070a14
//
//   Light:
//     Primary    Cyan          #0891b2 (deeper, accessible on white)
//     Surface    Off-white     #ffffff on #f5f7fa
//     Sidebar    Light slate   #ffffff
//
// Both palettes share accent colours (success/warning/error) and Inter font.
// ──────────────────────────────────────────────────────────────────────────

// Shared tokens that don't depend on background/text contrast.
const sharedTokens = {
  borderRadius:   10,
  borderRadiusLG: 14,
  borderRadiusSM: 6,
  borderRadiusXS: 4,

  fontFamily:
    'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontSize:         13,
  fontSizeHeading1: 32,
  fontSizeHeading2: 24,
  fontSizeHeading3: 19,
  fontSizeHeading4: 16,
  fontSizeHeading5: 14,
  lineHeight:       1.55,
  controlHeight:    34,
  controlHeightSM:  28,
};

const sharedComponents = {
  Button: {
    controlHeight:   34,
    controlHeightSM: 28,
    fontSize:        13,
    paddingInline:   16,
    fontWeight:      600,
  },
  Input: {
    controlHeight: 34,
    paddingInline: 12,
  },
  Select: {
    controlHeight: 34,
  },
  Form: {
    itemMarginBottom:     16,
    labelFontSize:        13,
    verticalLabelPadding: "0 0 4px",
  },
  Tabs: {
    titleFontSize:         13,
    horizontalItemPadding: "8px 18px",
  },
  Statistic: {
    titleFontSize:   12,
    contentFontSize: 28,
  },
  Badge: {
    statusSize: 7,
  },
};

// ── Dark theme ────────────────────────────────────────────────────────────
export const wansightTheme: ThemeConfig = {
  algorithm: theme.darkAlgorithm,
  token: {
    ...sharedTokens,
    colorPrimary: "#36c5f0",
    colorInfo:    "#36c5f0",
    colorLink:    "#36c5f0",
    colorSuccess: "#00d4a3",
    colorWarning: "#f7b955",
    colorError:   "#ff5d6c",

    colorBorder:          "#1f2a4a",
    colorBorderSecondary: "#1f2a4a",
    colorBgLayout:        "#070a14",
    colorBgContainer:     "#0b1020",
    colorBgElevated:      "#111833",
    colorBgBase:          "#070a14",
    colorText:            "#dde4f5",
    colorTextSecondary:   "#8592b8",
    colorTextHeading:     "#ffffff",
    colorTextPlaceholder: "#4a5672",
  },
  components: {
    ...sharedComponents,
    Layout: {
      headerBg:      "#070a14",
      headerHeight:  52,
      headerPadding: "0 24px",
      siderBg:       "#070a14",
      bodyBg:        "#070a14",
      footerBg:      "#0b1020",
      footerPadding: "10px 24px",
    },
    Menu: {
      itemBg:             "transparent",
      itemColor:          "#8592b8",
      itemSelectedBg:     "rgba(54,197,240,0.1)",
      itemSelectedColor:  "#36c5f0",
      itemHoverBg:        "rgba(255,255,255,0.04)",
      itemHoverColor:     "#dde4f5",
      itemHeight:         38,
      iconSize:           14,
      groupTitleColor:    "#2b3a63",
      groupTitleFontSize: 10,
    },
    Card: {
      headerBg:       "#0b1020",
      headerHeight:   44,
      headerFontSize: 14,
      paddingLG:      18,
      borderRadiusLG: 14,
    },
    Table: {
      headerBg:            "#0b1020",
      headerColor:         "#8592b8",
      headerSplitColor:    "#1f2a4a",
      borderColor:         "#1f2a4a",
      rowHoverBg:          "#111833",
      cellPaddingBlock:    9,
      cellPaddingInline:   12,
      cellPaddingBlockSM:  6,
      cellPaddingInlineSM: 8,
      cellFontSize:        13,
    },
    Button: {
      ...sharedComponents.Button,
      primaryShadow: "0 6px 16px rgba(54,197,240,0.25)",
    },
    Input: {
      ...sharedComponents.Input,
      activeBorderColor: "#36c5f0",
      hoverBorderColor:  "#5ee0ff",
    },
    Modal: {
      headerBg:       "#111833",
      titleFontSize:  15,
      titleColor:     "#ffffff",
      contentBg:      "#0b1020",
      borderRadiusLG: 14,
    },
    Tabs: {
      ...sharedComponents.Tabs,
      inkBarColor:        "#36c5f0",
      itemSelectedColor:  "#36c5f0",
      itemHoverColor:     "#5ee0ff",
    },
    Tag: {
      defaultBg:      "rgba(54,197,240,0.1)",
      defaultColor:   "#36c5f0",
      borderRadiusSM: 4,
    },
    Progress: {
      defaultColor: "#36c5f0",
    },
  },
};

// ── Light theme ───────────────────────────────────────────────────────────
// Soft slate-blue palette — designed to be readable for long shifts
// without eye strain. Surfaces use #f7f9fc / #eef2f7 instead of pure
// white. Body text is slate-800 (#2d3748) not black. Primary is muted
// cyan-800 (#0e7490) rather than vivid cyan-500. Tested for WCAG AA
// (≥4.5:1) on body text and AAA (≥7:1) on headings.
export const wansightThemeLight: ThemeConfig = {
  algorithm: theme.defaultAlgorithm,
  token: {
    ...sharedTokens,
    colorPrimary: "#0e7490",
    colorInfo:    "#0e7490",
    colorLink:    "#0e7490",
    colorSuccess: "#047857",
    colorWarning: "#b45309",
    colorError:   "#b91c1c",

    // Alert/Tag/Banner tints — AntD's defaultAlgorithm derives these from
    // colorInfo/Success/Warning/Error, but our base hues are saturated
    // (cyan-800, etc.) which produces dark muddy "tint" backgrounds. Pin
    // them to bright pastel values so info/success Alerts read as soft
    // pastel cards, not dark teal/olive blocks.
    colorInfoBg:    "#e0f2fe", // sky-100
    colorInfoBorder:"#7dd3fc", // sky-300
    colorSuccessBg: "#dcfce7", // green-100
    colorSuccessBorder: "#86efac", // green-300
    colorWarningBg: "#fef3c7", // amber-100
    colorWarningBorder: "#fde68a", // amber-200
    colorErrorBg:   "#fee2e2", // red-100
    colorErrorBorder:"#fecaca", // red-200

    colorBorder:          "#d6dee8",
    colorBorderSecondary: "#e4ebf3",
    colorBgLayout:        "#eef2f7",
    colorBgContainer:     "#f7f9fc",
    colorBgElevated:      "#f7f9fc",
    colorBgBase:          "#eef2f7",
    colorText:            "#2d3748",
    colorTextSecondary:   "#5d6b7e",
    colorTextHeading:     "#1a202c",
    colorTextPlaceholder: "#94a3b8",
  },
  components: {
    ...sharedComponents,
    Layout: {
      headerBg:      "#eef2f7",
      headerHeight:  52,
      headerPadding: "0 24px",
      siderBg:       "#eef2f7",
      bodyBg:        "#eef2f7",
      footerBg:      "#f7f9fc",
      footerPadding: "10px 24px",
    },
    Menu: {
      itemBg:             "transparent",
      itemColor:          "#5d6b7e",
      itemSelectedBg:     "rgba(14,116,144,0.10)",
      itemSelectedColor:  "#0e7490",
      itemHoverBg:        "rgba(45,55,72,0.04)",
      itemHoverColor:     "#1a202c",
      itemHeight:         38,
      iconSize:           14,
      groupTitleColor:    "#94a3b8",
      groupTitleFontSize: 10,
    },
    Card: {
      headerBg:       "#eef2f7",
      headerHeight:   44,
      headerFontSize: 14,
      paddingLG:      18,
      borderRadiusLG: 14,
    },
    Table: {
      headerBg:            "#eef2f7",
      headerColor:         "#5d6b7e",
      headerSplitColor:    "#d6dee8",
      borderColor:         "#d6dee8",
      rowHoverBg:          "#e4ebf3",
      cellPaddingBlock:    9,
      cellPaddingInline:   12,
      cellPaddingBlockSM:  6,
      cellPaddingInlineSM: 8,
      cellFontSize:        13,
    },
    Button: {
      ...sharedComponents.Button,
      primaryShadow: "0 4px 12px rgba(14,116,144,0.18)",
    },
    Input: {
      ...sharedComponents.Input,
      activeBorderColor: "#0e7490",
      hoverBorderColor:  "#0891b2",
    },
    Modal: {
      headerBg:       "#eef2f7",
      titleFontSize:  15,
      titleColor:     "#1a202c",
      contentBg:      "#f7f9fc",
      borderRadiusLG: 14,
    },
    Tabs: {
      ...sharedComponents.Tabs,
      inkBarColor:        "#0e7490",
      itemSelectedColor:  "#0e7490",
      itemHoverColor:     "#0891b2",
    },
    Tag: {
      defaultBg:      "rgba(14,116,144,0.10)",
      defaultColor:   "#0e7490",
      borderRadiusSM: 4,
    },
    Progress: {
      defaultColor: "#0e7490",
    },
  },
};
